import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Calendar, 
  MessageSquare, 
  HelpCircle, 
  LogOut, 
  Plus,
  Edit,
  Trash2,
  Trophy,
  BarChart3,
  Clock,
  Bell,
  CheckCircle,
  XCircle,
  Smartphone
} from 'lucide-react';
import { adminAuth, quizService, scheduleService, announcementService, contactService, pushNotificationService } from '../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState({
    totalQuestions: 0,
    totalEvents: 0,
    totalAnnouncements: 0,
    totalParticipants: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    const session = adminAuth.getSession();
    if (!session) {
      navigate('/admin/login');
      return;
    }
    setAdmin(session);
    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      const [questions, events, announcements, ranking] = await Promise.all([
        quizService.getQuestions(),
        scheduleService.getEvents(),
        announcementService.getAnnouncements(),
        quizService.getRanking()
      ]);

      setStats({
        totalQuestions: questions.data?.length || 0,
        totalEvents: events.data?.length || 0,
        totalAnnouncements: announcements.data?.length || 0,
        totalParticipants: ranking.data?.length || 0
      });
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    }
  };

  const handleLogout = () => {
    adminAuth.signOut();
    toast.success('Logout realizado com sucesso');
    navigate('/admin/login');
  };

  const StatCard = ({ icon: Icon, title, value, color, onClick }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      className={`bg-white rounded-xl p-6 shadow-lg border-l-4 ${color} ${onClick ? 'cursor-pointer hover:shadow-xl transition-shadow' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color.replace('border-l-', 'bg-').replace('-500', '-100')}`}>
          <Icon className={`w-6 h-6 ${color.replace('border-l-', 'text-')}`} />
        </div>
      </div>
    </motion.div>
  );

  const TabButton = ({ id, label, icon: Icon, active, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
        active 
          ? 'bg-blue-600 text-white shadow-lg' 
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
    </button>
  );

  if (!admin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 w-full">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b w-full">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo.png" 
                alt="Logo ICSA" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Painel Administrativo
                </h1>
                <p className="text-sm text-gray-500">
                  8ª Semana Acadêmica do ICSA
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{admin.name}</p>
                <p className="text-xs text-gray-500">{admin.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-red-600 transition-colors duration-200"
              >
                <LogOut className="w-5 h-5" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="py-6 w-full">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          {/* Navigation Tabs */}
          <div className="flex space-x-2 mb-8 bg-white p-2 rounded-xl shadow-sm">
            <TabButton
              id="overview"
              label="Visão Geral"
              icon={BarChart3}
              active={activeTab === 'overview'}
              onClick={setActiveTab}
            />
            <TabButton
              id="quiz"
              label="Quiz"
              icon={HelpCircle}
              active={activeTab === 'quiz'}
              onClick={setActiveTab}
            />
            <TabButton
              id="schedule"
              label="Programação"
              icon={Calendar}
              active={activeTab === 'schedule'}
              onClick={setActiveTab}
            />
            <TabButton
              id="announcements"
              label="Comunicados"
              icon={MessageSquare}
              active={activeTab === 'announcements'}
              onClick={setActiveTab}
            />
            <TabButton
              id="participants"
              label="Participantes"
              icon={Users}
              active={activeTab === 'participants'}
              onClick={setActiveTab}
            />
            <TabButton
              id="contact"
              label="Contato"
              icon={Users}
              active={activeTab === 'contact'}
              onClick={setActiveTab}
            />
            <TabButton
              id="notifications"
              label="Notificações"
              icon={Bell}
              active={activeTab === 'notifications'}
              onClick={setActiveTab}
            />
          </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={HelpCircle}
                title="Perguntas do Quiz"
                value={stats.totalQuestions}
                color="border-l-blue-500"
              />
              <StatCard
                icon={Calendar}
                title="Eventos"
                value={stats.totalEvents}
                color="border-l-green-500"
              />
              <StatCard
                icon={MessageSquare}
                title="Comunicados"
                value={stats.totalAnnouncements}
                color="border-l-yellow-500"
              />
              <StatCard
                icon={Users}
                title="Participantes"
                value={stats.totalParticipants}
                color="border-l-purple-500"
                onClick={() => setActiveTab('participants')}
              />
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Trophy className="w-5 h-5 mr-2 text-yellow-500" />
                Ações Rápidas
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setActiveTab('quiz')}
                  className="flex items-center justify-center space-x-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                >
                  <Plus className="w-5 h-5 text-blue-600" />
                  <span className="text-blue-600 font-medium">Nova Pergunta</span>
                </button>
                <button
                  onClick={() => setActiveTab('schedule')}
                  className="flex items-center justify-center space-x-2 p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors duration-200"
                >
                  <Plus className="w-5 h-5 text-green-600" />
                  <span className="text-green-600 font-medium">Novo Evento</span>
                </button>
                <button
                  onClick={() => setActiveTab('announcements')}
                  className="flex items-center justify-center space-x-2 p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-colors duration-200"
                >
                  <Plus className="w-5 h-5 text-yellow-600" />
                  <span className="text-yellow-600 font-medium">Novo Comunicado</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Quiz Tab */}
        {activeTab === 'quiz' && (
          <QuizManager />
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <ScheduleManager />
        )}
        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <AnnouncementManager />
        )}

        {/* Participants Tab */}
        {activeTab === 'participants' && (
          <ParticipantsManager />
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <ContactManager />
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <PushNotificationsManager />
        )}
      </div>
    </main>
  </div>
  );
};

// Componente para gerenciar quiz
const QuizManager = () => {
  const [questions, setQuestions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const { data, error } = await quizService.getQuestions();
      if (error) throw new Error(error);
      setQuestions(data || []);
    } catch (error) {
      toast.error('Erro ao carregar perguntas');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta pergunta?')) return;

    try {
      const { error } = await quizService.deleteQuestion(id);
      if (error) throw new Error(error);
      toast.success('Pergunta excluída com sucesso');
      loadQuestions();
    } catch (error) {
      toast.error('Erro ao excluir pergunta');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gerenciar Quiz</h2>
        <button
          onClick={() => {
            setEditingQuestion(null);
            setShowForm(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <Plus className="w-5 h-5" />
          <span>Nova Pergunta</span>
        </button>
      </div>

      {showForm && (
        <QuestionForm
          question={editingQuestion}
          onSave={() => {
            setShowForm(false);
            setEditingQuestion(null);
            loadQuestions();
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingQuestion(null);
          }}
        />
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {questions.length === 0 ? (
          <div className="text-center py-12">
            <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma pergunta cadastrada</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {questions.map((question, index) => (
              <div key={question.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {index + 1}. {question.question}
                    </h3>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className={`p-2 rounded ${question.correct_answer === 'A' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
                        A) {question.option_a}
                      </div>
                      <div className={`p-2 rounded ${question.correct_answer === 'B' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
                        B) {question.option_b}
                      </div>
                      <div className={`p-2 rounded ${question.correct_answer === 'C' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
                        C) {question.option_c}
                      </div>
                      <div className={`p-2 rounded ${question.correct_answer === 'D' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
                        D) {question.option_d}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">
                      Pontos: {question.points} | Resposta correta: {question.correct_answer}
                    </p>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => {
                        setEditingQuestion(question);
                        setShowForm(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(question.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Formulário para perguntas
const QuestionForm = ({ question, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    question: question?.question || '',
    option_a: question?.option_a || '',
    option_b: question?.option_b || '',
    option_c: question?.option_c || '',
    option_d: question?.option_d || '',
    correct_answer: question?.correct_answer || 'A',
    points: question?.points || 10
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (question) {
        const { error } = await quizService.updateQuestion(question.id, formData);
        if (error) throw new Error(error);
        toast.success('Pergunta atualizada com sucesso');
      } else {
        const { error } = await quizService.createQuestion(formData);
        if (error) throw new Error(error);
        toast.success('Pergunta criada com sucesso');
      }
      onSave();
    } catch (error) {
      toast.error('Erro ao salvar pergunta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {question ? 'Editar Pergunta' : 'Nova Pergunta'}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pergunta
          </label>
          <textarea
            value={formData.question}
            onChange={(e) => setFormData({ ...formData, question: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="3"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opção A
            </label>
            <input
              type="text"
              value={formData.option_a}
              onChange={(e) => setFormData({ ...formData, option_a: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opção B
            </label>
            <input
              type="text"
              value={formData.option_b}
              onChange={(e) => setFormData({ ...formData, option_b: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opção C
            </label>
            <input
              type="text"
              value={formData.option_c}
              onChange={(e) => setFormData({ ...formData, option_c: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opção D
            </label>
            <input
              type="text"
              value={formData.option_d}
              onChange={(e) => setFormData({ ...formData, option_d: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resposta Correta
            </label>
            <select
              value={formData.correct_answer}
              onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pontos
            </label>
            <input
              type="number"
              value={formData.points}
              onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              min="1"
              required
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Componente para gerenciar programação
const ScheduleManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const { data, error } = await scheduleService.getEvents();
      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      toast.error('Erro ao carregar eventos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = () => {
    setCurrentEvent(null);
    setShowForm(true);
  };

  const handleEditEvent = (event) => {
    setCurrentEvent(event);
    setShowForm(true);
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este evento?')) return;
    
    try {
      setLoading(true);
      const { error } = await scheduleService.deleteEvent(id);
      if (error) throw error;
      toast.success('Evento excluído com sucesso');
      loadEvents();
    } catch (error) {
      toast.error('Erro ao excluir evento');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvent = async (eventData) => {
    try {
      setLoading(true);
      if (currentEvent) {
        const { error } = await scheduleService.updateEvent(currentEvent.id, eventData);
        if (error) throw error;
        toast.success('Evento atualizado com sucesso');
      } else {
        const { error } = await scheduleService.createEvent(eventData);
        if (error) throw error;
        toast.success('Evento criado com sucesso');
      }
      setShowForm(false);
      loadEvents();
    } catch (error) {
      toast.error('Erro ao salvar evento');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  if (showForm) {
    return <EventForm event={currentEvent} onSave={handleSaveEvent} onCancel={() => setShowForm(false)} />;
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gerenciar Programação</h2>
        <button
          onClick={handleAddEvent}
          className="flex items-center space-x-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Novo Evento</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Nenhum evento cadastrado</p>
          <button
            onClick={handleAddEvent}
            className="mt-4 text-blue-600 hover:text-blue-800 transition-colors"
          >
            Adicionar o primeiro evento
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Título
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Local
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {events.map((event) => (
                <tr key={event.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{event.title}</div>
                    <div className="text-xs text-gray-500 truncate max-w-xs">{event.speaker}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(event.date)}</div>
                    <div className="text-xs text-gray-500">{event.start_time?.substring(0, 5)} - {event.end_time?.substring(0, 5)}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {event.location}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {event.event_type || 'palestra'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${event.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {event.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditEvent(event)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Formulário para eventos
const EventForm = ({ event, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    speaker: '',
    event_type: 'abertura',
    active: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (event) {
      // Formatar a data para o formato esperado pelo input date (YYYY-MM-DD)
      const formattedDate = event.date ? new Date(event.date).toISOString().split('T')[0] : '';
      
      setFormData({
        title: event.title || '',
        description: event.description || '',
        date: formattedDate,
        start_time: event.start_time || '',
        end_time: event.end_time || '',
        location: event.location || '',
        speaker: event.speaker || '',
        event_type: event.event_type || 'abertura',
        active: event.active !== undefined ? event.active : true
      });
    }
  }, [event]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSave(formData);
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {event ? 'Editar Evento' : 'Novo Evento'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Título do Evento *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data *
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Local *
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Horário de Início *
            </label>
            <input
              type="time"
              value={formData.start_time}
              onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Horário de Término *
            </label>
            <input
              type="time"
              value={formData.end_time}
              onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Evento
            </label>
            <select
              value={formData.event_type}
              onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="abertura">Abertura</option>
              <option value="palestra">Palestra</option>
              <option value="miniscurso">Miniscurso</option>
              <option value="oficina">Oficina</option>
              <option value="apresentacao">Apresentação de trabalho</option>
              <option value="encerramento">Encerramento</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Palestrante / Responsável
          </label>
          <input
            type="text"
            value={formData.speaker}
            onChange={(e) => setFormData({ ...formData, speaker: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descrição
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="4"
          ></textarea>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="active"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
            Evento ativo (visível para o público)
          </label>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

const AnnouncementManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [processingScheduled, setProcessingScheduled] = useState(false);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const { data, error } = await announcementService.getAnnouncements();
      if (error) throw error;
      setAnnouncements(data || []);
    } catch (error) {
      toast.error('Erro ao carregar comunicados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnnouncement = () => {
    setCurrentAnnouncement(null);
    setShowForm(true);
  };

  const handleEditAnnouncement = (announcement) => {
    setCurrentAnnouncement(announcement);
    setShowForm(true);
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este comunicado?')) return;
    
    try {
      setLoading(true);
      const { error } = await announcementService.deleteAnnouncement(id);
      if (error) throw error;
      toast.success('Comunicado excluído com sucesso');
      loadAnnouncements();
    } catch (error) {
      toast.error('Erro ao excluir comunicado');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAnnouncement = async (announcementData) => {
    try {
      setLoading(true);
      if (currentAnnouncement) {
        const { error } = await announcementService.updateAnnouncement(currentAnnouncement.id, announcementData);
        if (error) throw error;
        toast.success('Comunicado atualizado com sucesso');
      } else {
        const { error } = await announcementService.createAnnouncement(announcementData);
        if (error) throw error;
        toast.success('Comunicado criado com sucesso');
      }
      setShowForm(false);
      loadAnnouncements();
    } catch (error) {
      toast.error('Erro ao salvar comunicado');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleProcessScheduled = async () => {
    try {
      setProcessingScheduled(true);
      const { success, processed, error } = await announcementService.processScheduledAnnouncements();
      
      if (error) throw error;
      
      if (processed > 0) {
        toast.success(`${processed} comunicado(s) agendado(s) processado(s) com sucesso!`);
        loadAnnouncements();
      } else {
        toast.info('Não há comunicados agendados para processar no momento.');
      }
    } catch (error) {
      toast.error('Erro ao processar comunicados agendados');
      console.error(error);
    } finally {
      setProcessingScheduled(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (showForm) {
    return <AnnouncementForm announcement={currentAnnouncement} onSave={handleSaveAnnouncement} onCancel={() => setShowForm(false)} />;
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gerenciar Comunicados</h2>
        <div className="flex space-x-2">
          <button
            onClick={handleProcessScheduled}
            disabled={processingScheduled}
            className="flex items-center space-x-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            <Clock className="w-4 h-4" />
            <span>{processingScheduled ? 'Processando...' : 'Processar Agendados'}</span>
          </button>
          <button
            onClick={handleAddAnnouncement}
            className="flex items-center space-x-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Novo Comunicado</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Nenhum comunicado cadastrado</p>
          <button
            onClick={handleAddAnnouncement}
            className="mt-4 text-blue-600 hover:text-blue-800 transition-colors"
          >
            Adicionar o primeiro comunicado
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Título
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prioridade
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agendamento
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {announcements.map((announcement) => (
                <tr key={announcement.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-xs">{announcement.title}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {announcement.type || 'geral'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      announcement.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                      announcement.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      announcement.priority === 'normal' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {announcement.priority === 'urgent' ? 'Urgente' :
                       announcement.priority === 'high' ? 'Alta' :
                       announcement.priority === 'normal' ? 'Média' : 'Baixa'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(announcement.created_at)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${announcement.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {announcement.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {announcement.scheduled ? (
                      <div>
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Agendado
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {announcement.schedule_datetime ? formatDate(announcement.schedule_datetime) : 'Data não definida'}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">Não agendado</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditAnnouncement(announcement)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteAnnouncement(announcement.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Formulário para comunicados
const AnnouncementForm = ({ announcement, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'geral',
    priority: 'low',
    active: true,
    send_notification: false,
    scheduled: false,
    schedule_date: '',
    schedule_time: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (announcement) {
      // Formatar a data para o formato esperado pelo input date (YYYY-MM-DD)
      const formattedDate = announcement.schedule_date ? new Date(announcement.schedule_date).toISOString().split('T')[0] : '';
      
      setFormData({
        title: announcement.title || '',
        content: announcement.content || '',
        type: announcement.type || 'geral',
        priority: announcement.priority || 'low',
        active: announcement.active !== undefined ? announcement.active : true,
        send_notification: announcement.send_notification || false,
        scheduled: announcement.scheduled || false,
        schedule_date: formattedDate,
        schedule_time: announcement.schedule_time || ''
      });
    }
  }, [announcement]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSave(formData);
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {announcement ? 'Editar Comunicado' : 'Novo Comunicado'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Título do Comunicado *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Comunicado
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="geral">Geral</option>
              <option value="inscricoes">Inscrições</option>
              <option value="quiz">Quiz</option>
              <option value="alteracao">Alterações</option>
              <option value="material">Material</option>
              <option value="certificados">Certificados</option>
              <option value="transmissao">Transmissão</option>
              <option value="avaliacao">Avaliação</option>
              <option value="vagas">Vagas</option>
              <option value="encerramento">Encerramento</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prioridade
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="low">Baixa</option>
              <option value="normal">Média</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Conteúdo do Comunicado *
          </label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="6"
            required
          ></textarea>
        </div>

        <div className="flex flex-col space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
              Comunicado ativo (visível para o público)
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="send_notification"
              checked={formData.send_notification}
              onChange={(e) => setFormData({ ...formData, send_notification: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="send_notification" className="ml-2 block text-sm text-gray-900">
              Enviar notificação
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="scheduled"
              checked={formData.scheduled}
              onChange={(e) => setFormData({ ...formData, scheduled: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="scheduled" className="ml-2 block text-sm text-gray-900">
              Agendar publicação
            </label>
          </div>

          {formData.scheduled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pl-6 border-l-2 border-blue-100">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data de publicação
                </label>
                <input
                  type="date"
                  value={formData.schedule_date}
                  onChange={(e) => setFormData({ ...formData, schedule_date: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required={formData.scheduled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Horário de publicação
                </label>
                <input
                  type="time"
                  value={formData.schedule_time}
                  onChange={(e) => setFormData({ ...formData, schedule_time: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required={formData.scheduled}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Componente para gerenciar participantes
const ParticipantsManager = () => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadParticipants();
  }, []);

  const loadParticipants = async () => {
    try {
      setLoading(true);
      const { data, error } = await quizService.getRanking();
      if (error) throw new Error(error);
      setParticipants(data || []);
    } catch (error) {
      toast.error('Erro ao carregar participantes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (participantId, participantName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o participante "${participantName}"?`)) {
      return;
    }

    try {
      // Deletar via SQL direto
      const { error } = await quizService.deleteParticipant(participantId);
      if (error) throw new Error(error);
      
      toast.success('Participante excluído com sucesso');
      loadParticipants();
    } catch (error) {
      toast.error('Erro ao excluir participante');
      console.error(error);
    }
  };

  const handleViewDetails = (participant) => {
    setSelectedParticipant(participant);
    setShowDetails(true);
  };

  const formatarTempo = (segundos) => {
    if (!segundos || segundos === 0) return '--';
    const minutos = Math.floor(segundos / 60);
    const segsRestantes = segundos % 60;
    return `${minutos}:${segsRestantes < 10 ? '0' : ''}${segsRestantes}`;
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="w-6 h-6 mr-2 text-purple-600" />
              Participantes do Quiz
            </h2>
            <p className="text-gray-600 mt-1">
              Total de {participants.length} participante{participants.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de Participantes */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Posição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pontuação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tempo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {participants.map((participant, index) => (
                <tr key={participant.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {index === 0 && <Trophy className="w-5 h-5 text-golden-500 mr-2" />}
                      {index === 1 && <Trophy className="w-5 h-5 text-gray-400 mr-2" />}
                      {index === 2 && <Trophy className="w-5 h-5 text-amber-600 mr-2" />}
                      <span className="text-sm font-medium text-gray-900">#{index + 1}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{participant.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{participant.email || '--'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {participant.score} pontos
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <Clock className="w-4 h-4 mr-1 text-gray-400" />
                      {formatarTempo(participant.time_spent_seconds)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatarData(participant.completed_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleViewDetails(participant)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      title="Ver detalhes"
                    >
                      <Edit className="w-4 h-4 inline" />
                    </button>
                    <button
                      onClick={() => handleDelete(participant.id, participant.name)}
                      className="text-red-600 hover:text-red-900"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {participants.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhum participante encontrado</p>
          </div>
        )}
      </div>

      {/* Modal de Detalhes */}
      {showDetails && selectedParticipant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Detalhes do Participante</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <p className="text-lg font-semibold text-gray-900">{selectedParticipant.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-lg text-gray-900">{selectedParticipant.email || 'Não informado'}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-green-700 mb-1">Pontuação</label>
                  <p className="text-2xl font-bold text-green-600">{selectedParticipant.score}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-blue-700 mb-1">Tempo</label>
                  <p className="text-2xl font-bold text-blue-600">{formatarTempo(selectedParticipant.time_spent_seconds)}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-purple-700 mb-1">Posição</label>
                  <p className="text-2xl font-bold text-purple-600">
                    #{participants.findIndex(p => p.id === selectedParticipant.id) + 1}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Conclusão</label>
                <p className="text-lg text-gray-900">{formatarData(selectedParticipant.completed_at)}</p>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowDetails(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                Fechar
              </button>
              <button
                onClick={() => {
                  handleDelete(selectedParticipant.id, selectedParticipant.name);
                  setShowDetails(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
              >
                Excluir Participante
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

// Componente para gerenciar equipe de contato
const ContactManager = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const { data, error } = await contactService.getTeamMembers();
      if (error) throw error;
      setTeamMembers(data || []);
    } catch (error) {
      toast.error('Erro ao carregar membros da equipe');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    setCurrentMember(null);
    setShowForm(true);
  };

  const handleEditMember = (member) => {
    setCurrentMember(member);
    setShowForm(true);
  };

  const handleDeleteMember = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este membro?')) return;
    
    try {
      setLoading(true);
      const { error } = await contactService.deleteTeamMember(id);
      if (error) throw error;
      toast.success('Membro excluído com sucesso');
      loadTeamMembers();
    } catch (error) {
      toast.error('Erro ao excluir membro');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMember = async (memberData) => {
    try {
      setLoading(true);
      if (currentMember) {
        const { error } = await contactService.updateTeamMember(currentMember.id, memberData);
        if (error) throw error;
        toast.success('Membro atualizado com sucesso');
      } else {
        const { error } = await contactService.createTeamMember(memberData);
        if (error) throw error;
        toast.success('Membro criado com sucesso');
      }
      setShowForm(false);
      loadTeamMembers();
    } catch (error) {
      toast.error('Erro ao salvar membro');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (showForm) {
    return <ContactForm member={currentMember} onSave={handleSaveMember} onCancel={() => setShowForm(false)} />;
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gerenciar Equipe Organizadora</h2>
        <button
          onClick={handleAddMember}
          className="flex items-center space-x-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Novo Membro</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : teamMembers.length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Nenhum membro cadastrado</p>
          <button
            onClick={handleAddMember}
            className="mt-4 text-blue-600 hover:text-blue-800 transition-colors"
          >
            Adicionar o primeiro membro
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comissão
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  WhatsApp
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  E-mail
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teamMembers.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{member.name}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {member.commission}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {member.whatsapp || '--'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {member.email || '--'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditMember(member)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteMember(member.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Formulário para membros da equipe
const ContactForm = ({ member, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    commission: 'COORDENAÇÃO GERAL',
    whatsapp: '',
    email: '',
    photo_url: '',
    active: true
  });
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name || '',
        commission: member.commission || 'COORDENAÇÃO GERAL',
        whatsapp: member.whatsapp || '',
        email: member.email || '',
        photo_url: member.photo_url || '',
        active: member.active !== undefined ? member.active : true
      });
      setPhotoPreview(member.photo_url || '');
      setPhotoFile(null);
    }
  }, [member]);

  useEffect(() => {
    return () => {
      if (photoPreview && photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview(formData.photo_url || '');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem válido.');
      return;
    }

    if (photoPreview && photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }

    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleRemovePhoto = () => {
    if (photoPreview && photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(null);
    setPhotoPreview('');
    setFormData((prev) => ({ ...prev, photo_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let payload = { ...formData };

      if (photoFile) {
        try {
          const uploadedUrl = await contactService.uploadTeamMemberPhoto(photoFile);
          payload.photo_url = uploadedUrl;
        } catch (uploadError) {
          console.error(uploadError);
          toast.error('Erro ao enviar a foto. Tente novamente.');
          setLoading(false);
          return;
        }
      }

      await onSave(payload);
      if (photoPreview && photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {member ? 'Editar Membro' : 'Novo Membro'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome Completo *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Comissão *
          </label>
          <select
            value={formData.commission}
            onChange={(e) => setFormData({ ...formData, commission: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="COORDENAÇÃO GERAL">COORDENAÇÃO GERAL</option>
            <option value="COMISSÃO CIENTÍFICA">COMISSÃO CIENTÍFICA</option>
            <option value="COMISSÃO DE INFRAESTRUTURA">COMISSÃO DE INFRAESTRUTURA</option>
            <option value="COMISSÃO DE CREDENCIAMENTO">COMISSÃO DE CREDENCIAMENTO</option>
            <option value="COMISSÃO DE CULTURA">COMISSÃO DE CULTURA</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              WhatsApp *
            </label>
            <input
              type="text"
              value={formData.whatsapp}
              onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
              placeholder="94991234567"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-mail *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Foto do Membro
          </label>
          <div className="space-y-3">
            {photoPreview ? (
              <div className="flex items-center space-x-4">
                <img
                  src={photoPreview}
                  alt="Pré-visualização"
                  className="w-20 h-20 rounded-full object-cover border"
                />
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="px-3 py-2 text-sm text-red-600 hover:text-red-700"
                >
                  Remover foto
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Nenhuma foto selecionada. Você pode fazer upload de uma imagem ou deixar em branco.
              </p>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full"
            />

            <p className="text-xs text-gray-400">
              Formatos aceitos: JPG, PNG ou WEBP. Tamanho máximo recomendado: 2MB.
            </p>
          </div>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="active"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
            Membro ativo (visível no site)
          </label>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Componente para gerenciar notificações push
const PushNotificationsManager = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('stats'); // 'stats', 'devices', 'logs'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [subsResult, logsResult, statsResult] = await Promise.all([
        pushNotificationService.getSubscriptions(),
        pushNotificationService.getNotificationLogs(50),
        pushNotificationService.getNotificationStats()
      ]);

      if (subsResult.error) throw subsResult.error;
      if (logsResult.error) throw logsResult.error;
      if (statsResult.error) throw statsResult.error;

      setSubscriptions(subsResult.data || []);
      setLogs(logsResult.data || []);
      setStats(statsResult.data);
    } catch (error) {
      toast.error('Erro ao carregar dados de notificações');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubscription = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover este dispositivo?')) return;

    try {
      const { error } = await pushNotificationService.deleteSubscription(id);
      if (error) throw error;
      toast.success('Dispositivo removido com sucesso');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover dispositivo');
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Bell className="w-6 h-6 mr-2 text-blue-600" />
              Gerenciar Notificações Push
            </h2>
            <p className="text-gray-600 mt-1">
              Monitore dispositivos cadastrados e o histórico de envios
            </p>
          </div>
        </div>

        {/* Estatísticas */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Enviadas</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
                </div>
                <Bell className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Sucesso</p>
                  <p className="text-2xl font-bold text-green-700">{stats.success}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Falhas</p>
                  <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Taxa de Sucesso</p>
                  <p className="text-2xl font-bold text-purple-700">{stats.successRate}%</p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <div className="flex space-x-4 p-4">
            <button
              onClick={() => setActiveView('devices')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeView === 'devices'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              <span>Dispositivos ({subscriptions.length})</span>
            </button>
            <button
              onClick={() => setActiveView('logs')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeView === 'logs'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span>Histórico ({logs.length})</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* View de Dispositivos */}
          {activeView === 'devices' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID do Dispositivo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Cadastro
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Smartphone className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-sm font-mono text-gray-900">
                            {sub.id.substring(0, 8)}...
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(sub.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDeleteSubscription(sub.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Remover dispositivo"
                        >
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {subscriptions.length === 0 && (
                <div className="text-center py-12">
                  <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhum dispositivo cadastrado</p>
                </div>
              )}
            </div>
          )}

          {/* View de Logs */}
          {activeView === 'logs' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Título
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Comunicado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Envio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Erro
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {log.status === 'success' ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Sucesso
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3 mr-1" />
                            Falha
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{log.title}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">{log.body}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {log.announcements?.title || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(log.sent_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-red-600">
                        {log.error_message || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {logs.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhum log de notificação encontrado</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AdminDashboard;
